import { useSyncExternalStore } from "react";

/**
 * Whether this browser should be shown motion at all.
 *
 * Two unrelated reasons to say no, answered in one place because every animated thing in the app
 * needs both and they must never disagree — a page whose background froze but whose content still
 * slid into view would be worse than either behaviour on its own.
 *
 * IT LIVES IN THIS PACKAGE, not in `apps/app`, since feature 009. `SheenSurface` is here and needs the
 * same answer, and a package cannot import from an application. The alternative was a second copy of
 * these two conditions, which is exactly the disagreement the paragraph above rules out: the copy that
 * forgot `navigator.webdriver` would animate under Playwright and nowhere else, so it would be the
 * e2e suite that got slower rather than anything that looked wrong.
 *
 * ==========================================================================================
 * AND IT IS REACHED AS `@gladis/ui/motion`, WHICH IS THE PACKAGE'S ONLY NON-COMPONENT SUBPATH
 * ==========================================================================================
 *
 * `app-background.tsx` renders behind every page including sign-in, and it needs this answer before
 * anything else paints. Imported from the package's index it pulled the ENTIRE component barrel —
 * every control, the dialog, the toast provider, the chart — onto the first-paint path of every route,
 * and in development Vite has to transform all of it before the first render.
 *
 * That was measured rather than reasoned about. Its symptom was a test in another feature entirely:
 * `household-switching.spec.ts` began failing about one run in four, clicking the household switcher
 * and timing out waiting for a menu that had not rendered yet. Twelve runs on `develop` passed; two of
 * eight failed with the barrel import; eight of eight passed once the import was narrowed. A slow first
 * paint is not a correctness bug, which is exactly why it took a flake in an unrelated suite to find.
 */

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/**
 * A pointer that can hover and can be aimed precisely — a mouse or a trackpad, not a finger.
 *
 * Separate from the motion question and answered the same way, because a pointer-tracking effect on a
 * touch device is not "reduced" motion, it is motion with nothing to track: the highlight would jump to
 * wherever the last tap landed and stay there.
 */
const FINE_POINTER = "(hover: hover) and (pointer: fine)";

/**
 * Subscribes to a media query, for `useSyncExternalStore`.
 *
 * SUBSCRIBING MATTERS, and reading `matchMedia(...).matches` once would have been the shorter version.
 * Both of these change while the app is open: a visitor turns reduced motion on in their system
 * settings, or plugs a mouse into a tablet. A value read once at mount then describes a device the
 * visitor no longer has, and the effect gated on it never reconsiders.
 */
function subscribeTo(query: string): (onChange: () => void) => () => void {
  return (onChange) => {
    const list = window.matchMedia(query);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  };
}

const subscribe = subscribeTo(REDUCED_MOTION);
const subscribeToPointer = subscribeTo(FINE_POINTER);

function snapshot(): boolean {
  /*
   * REASON ONE: the visitor asked for less motion. Not negotiable.
   *
   * REASON TWO: `navigator.webdriver` is true only under browser automation — Playwright, Selenium
   * — and never for a real visitor. Animation costs the e2e suite real time twice over: headless
   * Chromium has no GPU, so the WebGL background renders in software, and Playwright waits for an
   * element to stop moving before it will click it, so every reveal is added to every interaction.
   * With both on, the suite ran three to nine times longer and began timing tests out. None of
   * those journeys is testing the animation.
   */
  return window.matchMedia(REDUCED_MOTION).matches || navigator.webdriver;
}

/** Server/prerender has no `window`. Nothing renders there today; the safe default is still. */
function serverSnapshot(): boolean {
  return true;
}

function pointerSnapshot(): boolean {
  return window.matchMedia(FINE_POINTER).matches;
}

/** Server/prerender: assume no fine pointer, so nothing schedules tracking it cannot do. */
function noPointerOnServer(): boolean {
  return false;
}

export function useMotionDisabled(): boolean {
  return useSyncExternalStore(subscribe, snapshot, serverSnapshot);
}

/**
 * Whether the visitor has a pointer worth tracking.
 *
 * A separate hook rather than a second condition folded into `useMotionDisabled`, because the two mean
 * different things to a consumer: reduced motion says *do less*, and no fine pointer says *there is
 * nothing here to follow*. A surface that reacts to a pointer needs both answers; a fade-in on mount
 * needs only the first, and folding them together would have stopped it fading in on a phone.
 */
export function useFinePointer(): boolean {
  return useSyncExternalStore(subscribeToPointer, pointerSnapshot, noPointerOnServer);
}
